import React, { useEffect, useState } from "react";
import { CyberpunkHeroBackground } from "./CyberpunkHeroBackground";
import { sanitizeTokens, safeLookup } from './ui/chart';

const TOKENS = [
  { symbol: "SOL", name: "Solana", icon: "/images/coins/svg/SOL.svg" },
  { symbol: "WIF", name: "WIF", icon: "/images/coins/svg/WIF.svg" },
  { symbol: "PEPE", name: "PEPE", icon: "/images/coins/PEPE.svg" },
  { symbol: "BONK", name: "BONK", icon: "/images/coins/svg/BONK.svg" },
  { symbol: "JUP", name: "JUP", icon: "/images/coins/svg/JUP.svg" },
  { symbol: "ORCA", name: "ORCA", icon: "/images/coins/svg/ORCA.svg" },
];

console.log('[3DHeroSection] TOKENS:', TOKENS);
const sanitizedTokens = sanitizeTokens(TOKENS);
console.log('[3DHeroSection] sanitizedTokens:', sanitizedTokens);

const style = `
  @keyframes curtain {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(0); }
  }
  .curtain-effect {
    animation: curtain 1.5s ease-out forwards;
  }
`;

export function ThreeDHeroSection() {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchPrices = async () => {
      setLoading(true);
      let price: number | null = null;
      try {
        const res = await fetch("/live");
        const data = await res.json();
        if (typeof data?.prices?.SOL === "number" && !isNaN(data.prices.SOL)) {
          price = data.prices.SOL;
        }
      } catch {}
      if (isMounted) {
        setSolPrice(price);
        setLoading(false);
      }
    };
    fetchPrices();
  }, []);

  return (
    <>
      <style>
        {style}
      </style>
      <section className="relative flex flex-col items-center justify-center min-h-[80vh] py-24 overflow-hidden" style={{
        background: `linear-gradient(180deg, #050012 0%, #0f0026 50%, #1a0033 100%)`,
        backgroundBlendMode: 'lighten',
        backgroundColor: '#050012'
      }}>
        <div className="w-full px-4 flex flex-col items-center relative z-10">
          {/* Animated Cyberpunk Background */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <CyberpunkHeroBackground />
          </div>
          {/* Mascot with Solana gradient circle and glow */}
          <div className="relative mb-8 mt-2.5 flex flex-col items-center" style={{ marginTop: '10px' }}>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full z-0"
              style={{
                background: 'conic-gradient(from 120deg, #14F195 0deg, #19FB9B 90deg, #9945FF 210deg, #14F195 360deg)',
                boxShadow: '0 0 80px 30px #9945FF55, 0 0 120px 40px #14F19533',
                opacity: 0.85,
                filter: 'blur(2px)'
              }}
            />
            <img
              src="/images/branding/SOLANA-VOLUME-BOT-MASCOTTE.avif"
              alt="Solana Volume Bot Mascot"
              className="w-40 h-40 md:w-56 md:h-56 rounded-full shadow-2xl relative z-10"
              style={{
                borderRadius: '50%',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
                border: 'none'
              }}
            />
          </div>
          {/* Headline with high-contrast neon gradients */}
          <h1 className="text-4xl md:text-6xl font-bold text-center mb-4 leading-tight bg-gradient-to-r from-[#00FFA3] via-[#40e0ff] to-[#DC1FFF] bg-clip-text text-transparent">
            Boost <span className="italic">Your</span> Solana Token's Visibility
          </h1>
          {/* Subheadline with pure white text for contrast */}
          <p className="max-w-2xl mx-auto text-lg md:text-2xl font-medium text-center mb-6 bg-gradient-to-r from-[#00FFA3] via-[#40e0ff] to-[#DC1FFF] bg-clip-text text-transparent">
            Get trending on <span className="font-bold" style={{ color: '#19FBFF' }}>DEXTOOLS</span>, <span className="font-bold" style={{ color: '#14F195' }}>PUMP.FUN</span>, and <span className="font-bold" style={{ color: '#FF7C2A' }}>DEXSCREENER</span> with the most advanced and secure Solana Volume Bot.
          </p>
          {/* Security/UX Badge */}
          {/* Removed badge as requested */}
          {/* CTAs */}
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-8">
            <a
              href="#pricing"
              className="relative z-10 inline-block px-12 py-5 rounded-full text-xl font-bold text-white bg-gradient-to-r from-[#0b3a2e] via-[#0a3a4a] to-[#2a1a3a] shadow-xl border-2 border-[#2affb7]/40 transition-transform duration-200 hover:scale-105 hover:shadow-[0_0_32px_8px_#2affb755,0_0_0_4px_#c299ff33] focus:outline-none focus:ring-4 focus:ring-[#2affb7]/40"
              style={{
                boxShadow: '0 4px 32px 0 #2affb733, 0 0 0 4px #c299ff33',
                background: 'linear-gradient(90deg, #0b3a2e 0%, #0a3a4a 50%, #2a1a3a 100%)',
                border: '2px solid #2affb744',
              }}
            >
              🚀 Start 7-Day Free Trial
              <span className="absolute inset-0 rounded-full pointer-events-none animate-pulse bg-gradient-to-r from-[#2affb733] via-[#00eaff22] to-[#c299ff33] opacity-40" />
            </a>
            <a
              href="#demo"
              className="relative z-10 inline-block px-8 py-5 rounded-full text-lg font-semibold text-white bg-gradient-to-r from-[#101c2c] to-[#23272e] border-2 border-[#00eaff]/30 shadow hover:scale-105 hover:shadow-xl transition-transform duration-200">
              🎥 See How It Works
            </a>
          </div>
          {/* Trust Badge with white text for contrast */}
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2 px-6 py-2 rounded-full border border-[#2affb7]/30 text-base md:text-lg font-semibold bg-gradient-to-r from-[#00FFA3] via-[#40e0ff] to-[#DC1FFF] bg-clip-text text-transparent">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#2affb7" opacity="0.15"/><path d="M7 13l3 3 7-7" stroke="#2affb7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Trusted by 100+ Solana projects
            </span>
          </div>
          {/* Price Ticker - 100% stable, no animation, no glow */}
          {/* Removed price ticker as requested */}
        </div>
      </section>
    </>
  );
}